import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useState } from "react";
import { Campaign, CampaignStatus } from "schemas/dashboard";

const LOCKED_SETTINGS_HINT =
  "Once launched, a campaign's dates and schedule are locked. You can " +
  "rename, pause, or resume it — to change timing, duplicate it into a " +
  "new campaign.";

const COMPLETE_SETTINGS_HINT =
  "This campaign is complete. You can rename it, or duplicate it into a " +
  "new campaign to run it again.";

interface Props {
  campaign: Campaign;
  onRename: () => void;
  onEditSettings: () => void;
  onPause: () => void;
  onResume: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

export default function CampaignActionsMenu({
  campaign,
  onRename,
  onEditSettings,
  onPause,
  onResume,
  onDuplicate,
  onArchive,
  onDelete,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const run = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    action();
  };

  const canPause = campaign.status === CampaignStatus.in_progress;
  const canResume = campaign.status === CampaignStatus.paused;
  const isComplete = campaign.status === CampaignStatus.complete;
  const isStarted =
    campaign.status === CampaignStatus.in_progress ||
    campaign.status === CampaignStatus.paused ||
    isComplete;
  const settingsHint = isComplete
    ? COMPLETE_SETTINGS_HINT
    : LOCKED_SETTINGS_HINT;

  return (
    <>
      <Tooltip title="More actions" placement="top">
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{ color: grey[400], "&:hover": { color: grey[700] } }}
        >
          <Box
            component="i"
            className="fa-regular fa-ellipsis"
            sx={{ fontSize: 15 }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableRestoreFocus
        sx={{ "& .MuiPaper-root": { minWidth: 220 } }}
      >
        <MenuItem onClick={run(onRename)}>
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-pen" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>

        {isStarted ? (
          <MenuItem disabled sx={{ whiteSpace: "normal", maxWidth: 280 }}>
            <ListItemIcon>
              <Box component="i" className="fa-regular fa-gear" />
            </ListItemIcon>
            <ListItemText primary="Edit Settings" secondary={settingsHint} />
          </MenuItem>
        ) : (
          <MenuItem onClick={run(onEditSettings)}>
            <ListItemIcon>
              <Box component="i" className="fa-regular fa-gear" />
            </ListItemIcon>
            <ListItemText>Edit Settings</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {canPause && (
          <MenuItem onClick={run(onPause)}>
            <ListItemIcon>
              <Box component="i" className="fa-regular fa-pause" />
            </ListItemIcon>
            <ListItemText>Pause Campaign</ListItemText>
          </MenuItem>
        )}
        {canResume && (
          <MenuItem onClick={run(onResume)}>
            <ListItemIcon>
              <Box component="i" className="fa-regular fa-play" />
            </ListItemIcon>
            <ListItemText>Resume Campaign</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={run(onDuplicate)}>
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-copy" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={run(onArchive)}>
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-box-archive" />
          </ListItemIcon>
          <ListItemText>Archive</ListItemText>
        </MenuItem>

        <MenuItem onClick={run(onDelete)} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <Box
              component="i"
              className="fa-regular fa-trash"
              sx={{ color: "error.main" }}
            />
          </ListItemIcon>
          <ListItemText>Delete campaign</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
